import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Card, CardContent, CardHeader, Divider, Stack } from '@packages/shared';
import { defineMessages, useIntl } from 'react-intl';
import { AvailabilityInfo } from '../AvailabilityInfo';
import { CarrierDeliveryInfo } from '../CarrierDeliveryInfo';
import { ExpressServiceInfo } from '../ExpressServiceInfo';

const messages = defineMessages({
  deliverycardHeadline: {
    id: 'delivery.delivery-card.headline',
    defaultMessage: 'Lieferinformationen',
  },
});

const DeliveryFragment = graphql(/* GraphQL */ `
  fragment DeliveryFragment on Product {
    ...AvailabilityInfoFragment
    availability {
      hasExpressService
    }
    delivery {
      deliverySize
      ...CarrierDeliveryInfoFragment
    }
  }
`);

type DeliveryProps = {
  productData: FragmentType<typeof DeliveryFragment>;
};

/**
 * This component is a wrapper to show all delivery infos for a given product.
 * Its subcomponents are:
 * <ul>
 *  <li>`AvailabilityInfo`</li>
 *  <li>`CarrierDeliveryInfo`</li>
 *  <li>`ExpressServiceInfo`</li>
 * </ul>
 */
export const DeliveryInfoCard = ({ productData }: DeliveryProps) => {
  const product = getFragmentData(DeliveryFragment, productData);

  const {
    delivery: { deliverySize },
    availability: { hasExpressService },
  } = product;

  const { formatMessage } = useIntl();
  return (
    <Card>
      <CardHeader
        title={formatMessage(messages.deliverycardHeadline)}
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <Stack spacing={1}>
          <AvailabilityInfo availabilityInfoData={product} />
          {/* there is no carrier delivery for normal sized products (size === "DEFAULT"), they are sent by standard delivery */}
          {deliverySize !== 'DEFAULT' && <CarrierDeliveryInfo deliveryData={product.delivery} />}
          {hasExpressService && (
            <>
              <Divider />
              <ExpressServiceInfo />
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
